import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import agent from '../../API/apiCalls';
import { UserContext, UserPayload } from '../../Context/userContext';
import { GetFirstDateOfMonth, GetTodayDate } from '../../utils/getTodayDate';
import { AgencyPageStyle } from './agencyPageStyle';
import { GlobalTab } from '../../Components/global/globalTab/GlobalTab';
import { AllAgencyTable } from '../../Components/agenciesTable/allAgencyTable';
import { AgencyHierachyTable } from '../../Components/agenciesTable/agencyHierachyTable';
import { AgencyHierachyData, AgencyHierachyDataResponse, AvailableProductsList, CommonResponseModel, DashboardDataResponse } from '../../Models/tableDetailsModel';
import { GetFormatTitle } from '../../Components/tableComponent/getTableColumn/getFormattedData';
import GlobalDatepicker from '../../Components/global/globalDatepicker/GlobalDatepicker';
import dayjs from 'dayjs';
import { DateFormat } from '../../constants/dateFormat';
import ProductFilterMenu from '../../Components/agenciesTable/productFilterMenu';
import { useAgencyInfo } from '../../Context/agencyContext';

const tabList = [
    {
        id: 0,
        name: "Agency Hierarchy"
    },
    {
        id: 1,
        name: "View All Agency"
    },
]

const Agencies = () => {

    const { tableContainerItem, dashboardPageMainContainer, filterIconItem } = AgencyPageStyle().classes

    const { setShowLoader } = React.useContext(UserContext) as UserPayload;
    const { agencyHierachyResponse, setAgencyHierachyResponse, allAgencyData, setAllAgencyData, setProductFilterList, productFilterList } = useAgencyInfo()

    const [dateRange, setDateRange] = useState<string[]>([GetFirstDateOfMonth(), GetTodayDate()])
    const [currentTab, setCurrentTab] = useState(0)
    const [hierachyHeaderName, setHierachyHeaderName] = useState<(keyof AgencyHierachyData)[] | null>(null)

    function transformData(data: AgencyHierachyData[]): AgencyHierachyData[] {
        function moveApplicationsDetails(userRow: AgencyHierachyData): AgencyHierachyData {
            const { applicationsDetails, children, ...rest } = userRow;

            const merged = applicationsDetails
                ? {
                    ...rest,
                    ...applicationsDetails,
                    openChild: 'childClosed' as const,
                }
                : { ...rest };

            const updatedChildren = children?.length
                ? children.map(child => moveApplicationsDetails(child))
                : [];

            return {
                ...merged,
                children: updatedChildren,
            };
        }

        return data.map(userRow => moveApplicationsDetails(userRow));
    }

    const GetSelectedProducts = () => {
        const listOfIds = productFilterList?.map((product) => product.flagSelected ? product.productId : null)
        if (!listOfIds) {
            return []
        }
        return (listOfIds.filter((productId): productId is string => productId !== null))
    }

    useEffect(() => {
        if (!agencyHierachyResponse || agencyHierachyResponse?.agentHierarchyData.length === 0) {
            return
        }
        const formattedKeys = Object.keys(agencyHierachyResponse.agentHierarchyData[0]).map(key => GetFormatTitle(key as keyof AgencyHierachyData));
        setHierachyHeaderName(formattedKeys as (keyof AgencyHierachyData)[]);
    }, [agencyHierachyResponse])

    useEffect(() => {
        (async () => {
            let response = await GetAgencyData(null, null, [])
            if (response) {
                response.availableProducts.forEach((product: AvailableProductsList) => {
                    product['flagSelected'] = null
                })
                setProductFilterList(response.availableProducts)
            }
        })()
    }, [])

    const GetAllAgencyData = async (start: string, end: string, productIds: string[]) => {
        try {
            setShowLoader(true);
            let response: CommonResponseModel<DashboardDataResponse> = await agent.GetDashboardData.get(start, end, productIds)
            if (response.flagStatus) {
                let updatedResponse = response.value;
                if (updatedResponse.totalDashboardDataResp !== null) {
                    updatedResponse.totalDashboardDataResp.name = 'Total'
                    updatedResponse.agencyDashboardDataResp.push(updatedResponse.totalDashboardDataResp)
                }
                setAllAgencyData(updatedResponse)
                return updatedResponse
            }
        } catch (error) {
            console.log(error)
        } finally {
            setShowLoader(false);
        }
    }

    const GetAgencyHierachyData = async (start: string, end: string, productIds: string[]) => {
        try {
            setShowLoader(true);
            let response: CommonResponseModel<AgencyHierachyDataResponse> = await agent.GetDashboardHierarchyData.get(start, end, productIds)
            if (response.flagStatus) {
                response.value.agentHierarchyData = transformData(response.value.agentHierarchyData)
                setAgencyHierachyResponse(response.value)
                return response.value
            }
        } catch (error) {
            console.log(error)
        } finally {
            setShowLoader(false);
        }
    }

    const GetAgencyData = async (start: string | null, end: string | null, productIds: string[] | null, tabNumber?: number) => {
        return (tabNumber !== undefined ? tabNumber : currentTab) === 0 ?
            GetAgencyHierachyData(start ?? dateRange[0], end ?? dateRange[1], productIds ?? GetSelectedProducts()) :
            GetAllAgencyData(start ?? dateRange[0], end ?? dateRange[1], productIds ?? GetSelectedProducts())
    }

    const ChangeDateRange = async (date: any, dateStrings: string[]) => {
        setDateRange(dateStrings)
        GetAgencyData(dateStrings[0], dateStrings[1], null)
    }

    const ChangeTab = async (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
        await GetAgencyData(null, null, null, newValue)
    }

    return (
        <Grid className={dashboardPageMainContainer} container direction="column" wrap="nowrap">
            <Grid item sx={{ flexShrink: 0, width: '100%', mb: 2 }}>
                <Grid container alignItems={"center"} justifyContent={"space-between"}>
                    <Grid item>
                        <GlobalTab
                            currentTab={currentTab}
                            ChangeTab={ChangeTab}
                            tabList={tabList}
                        />
                    </Grid>
                    <Grid item>
                        <Grid container alignItems={"center"}>
                            <Grid item>
                                <GlobalDatepicker
                                    value={dateRange}
                                    onChange={(date: any, dateStrings: string[]) => { ChangeDateRange(date, dateStrings) }}
                                    maxDate={dayjs(GetTodayDate(), DateFormat.MM_DD_YYYY)}
                                />
                            </Grid>
                            <Grid item className={filterIconItem}>
                                <ProductFilterMenu GetAgencyData={GetAgencyData} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                item
                className={tableContainerItem}
                sx={{ flex: '1 1 0', minHeight: 0, maxWidth: '100%', width: '100%' }}
            >
                {(allAgencyData !== null && currentTab === 1) &&
                    <AllAgencyTable />
                }
                {(agencyHierachyResponse !== null && currentTab === 0) &&
                    <AgencyHierachyTable hierachyHeaderName={hierachyHeaderName} />
                }
            </Grid>
        </Grid>
    )
}

export default Agencies 